import { useContext } from 'react';
import { Menu } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Permissions } from "@liveline/ll_node_common";
import { Action, LivelineSubject } from "@liveline/ll_node_common/lib/Permissions/types";
import './menu.scss';
import { userSelectionActions } from '../../../state/features/userSelectionReducer';
import { AbilityContext, } from '../Can';

interface Facility {
    id: number;
    text: string;
    livelineFacilityId?: string;
}

interface FacilityList {
    data: Facility[];
}

interface RootState {
    facilityList: FacilityList;
}

interface MenuComponentProps {
    theme?: 'light' | 'dark';
    style?: React.CSSProperties;
    mode?: 'vertical' | 'horizontal' | 'inline';
    defaultSelectedKeys?: string[];
    openKeys?: string[];
    items?: any[];
    onOpenChange?: (openKeys: string[]) => void;
    closeMenu?: () => void;
    onSelectHandler?: () => void;
}

interface MenuClickInfo {
    key: string;
    keyPath: string[];
    item: React.ReactInstance;
    domEvent: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>;
}

export const menuItemIsDisabled = (
    menuItemKey: string, 
    ability: any, 
    facilityId: string | undefined
): boolean => {
    return ability && facilityId 
        ? !Permissions.checkAbility(ability, Action.VIEW, menuItemKey as LivelineSubject, { facilityId }) 
        : false;
};

const MenuComponent: React.FC<MenuComponentProps> = ({
    theme = 'light',
    style = {},
    mode = 'inline',
    defaultSelectedKeys = [],
    openKeys = [],
    items = [],
    onOpenChange = () => {},
    closeMenu = () => {},
    onSelectHandler = () => {}
}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const ability = useContext(AbilityContext);
    const facilityList = useSelector((state: RootState) => state?.facilityList);

    const handleMenuClick = (e: MenuClickInfo): void => {
        if (e.key === 'documentation') {
            navigate(e?.key);
            window.open('https://customerdocs.liveline.cloud/index.html', '_blank');
        } else if (e.key === 'dashboard') {
            navigate('/');
        } else if (e.key === 'contact_us') {
            navigate(e?.key);
            window.open('https://liveline.atlassian.net/servicedesk/customer/portals', '_blank');
        } else if (e?.key.startsWith('facility_selector')) {
            const matches = e.key.match(/(?<=facility_selector\/)[\S]*/);

            if (matches && matches.length) {
                const id = matches[0];

                const selectedFacility = facilityList?.data?.find((f: Facility) => f.id == parseInt(id));

                if (selectedFacility) {
                    const currPage = defaultSelectedKeys[0];
                    dispatch(userSelectionActions.setUserSelection({
                        facility: selectedFacility,
                        cell: {},
                        product: {},
                    }));
                    
                    if (menuItemIsDisabled(currPage, ability, selectedFacility.livelineFacilityId)) {
                        navigate('/');
                    }

                    closeMenu();
                }
            }
        } else {
            navigate(e?.key);
        }
    };

    return (
        <Menu
            theme={theme}
            style={style}
            onClick={handleMenuClick}
            mode={mode}
            selectedKeys={defaultSelectedKeys}
            items={items}
            openKeys={openKeys}
            onOpenChange={onOpenChange}
            onSelect={onSelectHandler}
        />
    );
};

export default MenuComponent;